import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../context/casl/AbilityContext";
import { cn } from "./ui/utils";
import {
  Settings, LogOut, ChevronRight, ChevronDown,
} from "lucide-react";
import { Button } from "./ui/button";

import { ACTIONS, RESOURCES } from "../constants/permissions.constants";
import {
  getNavCategories,
  canRenderNavLink,
  canRenderNavCategory,
  filterNavLinks,
  NavLink,
  NavCategory,
} from "../constants/navigation";
import { useNavigationExtras } from "../hooks/useNavigationExtras";
import { useStockYards } from "../hooks/useStockYards";

export const Sidebar = ({
  isMobile,
  setIsMobileSidebarOpen,
  isCollapsed = false
}: {
  isMobile?: boolean;
  setIsMobileSidebarOpen?: (open: boolean) => void;
  isCollapsed?: boolean;
}) => {
  const { currentUser, logout } = useAuth();
  const location = useLocation();
  const ability = useAbility(AbilityContext);

  const [isRetiredExpanded, setIsRetiredExpanded] = React.useState(false);
  const { activeDesks, retiredDesks, hasOnboarding } = useNavigationExtras();

  const canSeeStock = ability.can(ACTIONS.READ, RESOURCES.STOCK);
  // STOCK yard nodes are data-driven (STOCK-MENU.md §1): the API returns only the yards
  // this user may see, and the shared hook hands the same list to the m.idarah.io grid.
  const stockYards = useStockYards(canSeeStock);
  const openYards = stockYards.filter(y => y.status !== 'closed');

  /**
   * Group expansion. Groups are collapsed by default, except the Settings group when the
   * user is already inside it, and the sole yard when the user has exactly one — a
   * yardsman who only ever works one yard should not have to open it every time (§4).
   */
  const [expandedGroups, setExpandedGroups] = React.useState<Set<string>>(
    () => new Set(
      [
        location.pathname.startsWith('/stock/settings') ? '/stock/settings/yards' : null,
        // Learning (LEARNING.md §8) follows the same rule as Settings: expanded whenever
        // the user is already inside it, so the four sub-areas are visible on arrival
        // rather than behind a click. The group's href is its first child.
        location.pathname.startsWith('/learning') || location.pathname.startsWith('/learning-admin')
          ? '/learning/courses'
          : null,
      ].filter((href): href is string => Boolean(href))
    )
  );

  const soleOpenYardHref = openYards.length === 1 ? `/stock/yards/${openYards[0].id}/deliveries` : null;
  React.useEffect(() => {
    if (!soleOpenYardHref) return;
    setExpandedGroups(prev => prev.has(soleOpenYardHref) ? prev : new Set(prev).add(soleOpenYardHref));
  }, [soleOpenYardHref]);

  const toggleGroup = (href: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(href)) next.delete(href); else next.add(href);
      return next;
    });
  };

  // Categorized navigation structure from the shared registry — the desktop tree and the
  // m.idarah.io grid render the same nodes under the same visibility rules.
  const navCategories = getNavCategories({
    currentUserId: currentUser?.id,
    activeDesks,
    retiredDesks,
    stockYards,
  });

  // Fixed bottom items
  const settingsLink: NavLink = {
    href: "/settings",
    label: "Settings",
    icon: <Settings size={20} />,
  };

  const handleLinkClick = () => {
    if (isMobile && setIsMobileSidebarOpen) {
      setIsMobileSidebarOpen(false);
    }
  };

  const getIdarahText = () => {
    if (window.location.hostname !== 'localhost') return "idarah";
    switch (currentUser?.userRole) {
      case "Admin": return "idarah-a";
      case "Director": return "idarah-d";
      case "Manager": return "idarah-m";
      case "Worker": return "idarah-w";
      default: return "idarah";
    }
  };

  const checkActive = (link: NavLink) => {
    const { pathname } = location;
    // Exact match
    if (pathname === link.href) return true;

    // Special cases that should override sub-path matching
    // Prevent People tab from highlighting when on My Profile
    if (link.href === "/people" && currentUser?.id && pathname.startsWith(`/people/${currentUser.id}`)) {
      return false;
    }

    // Sub-path match (e.g. /service-desk/3/edit should highlight /service-desk/3)
    // We add a trailing slash to the check to ensure we match whole path segments only.
    // This prevents /service-desk/37 from incorrectly highlighting /service-desk/3.
    if (!link.exact && link.href !== "/dashboard" && pathname.startsWith(link.href + "/")) {
      return true;
    }

    // Highlight My Policies when signing a memo
    if (link.href === "/my-policies" && pathname.startsWith("/memo-sign")) {
      return true;
    }

    return false;
  };

  const filterCtx = { ability, currentUser, hasOnboarding };

  const canRenderLink = (link: NavLink) => canRenderNavLink(link, filterCtx);

  const renderLink = (link: NavLink, className = "") => {
    if (!canRenderLink(link)) return null;

    const isActive = checkActive(link);

    return (
      <li key={link.href + link.label}>
        <Link
          to={link.href}
          onClick={handleLinkClick}
          className={cn(
            "flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-[14px]",
            isCollapsed ? "justify-center" : "",
            isActive
              ? "bg-selection text-link font-medium"
              : "text-muted-foreground hover:bg-accent hover:text-foreground",
            className
          )}
          title={isCollapsed ? link.label : undefined}
        >
          <span className={cn(
            "flex-shrink-0",
            isActive ? "text-link" : "text-muted-foreground"
          )}>{link.icon}</span>
          {!isCollapsed && <span className="truncate flex-1">{link.label}</span>}
          {!isCollapsed && link.badge !== undefined && (
            <span className="px-1.5 py-0.5 text-[10px] font-bold bg-muted text-muted-foreground rounded-sm">
              {link.badge}
            </span>
          )}
        </Link>
      </li>
    );
  };

  /**
   * A link with children is a disclosure group — a stock yard, Stock Settings, Closed
   * Yards, Retired Desks. The row expands rather than navigating, and it reads as active
   * while any of its children is the current page.
   */
  const renderGroup = (link: NavLink, children: NavLink[]) => {
    const isActive = checkActive(link) || children.some(checkActive);
    const isExpanded = expandedGroups.has(link.href);

    return (
      <li key={link.href + link.label}>
        <div
          onClick={() => toggleGroup(link.href)}
          className={cn(
            "flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-[14px] cursor-pointer group",
            isActive
              ? "bg-selection text-link font-medium"
              : "text-muted-foreground hover:bg-accent hover:text-foreground"
          )}
          title={isCollapsed ? link.label : undefined}
        >
          <span className="flex-shrink-0 w-4 -ml-1 text-muted-foreground group-hover:text-foreground">
            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </span>
          <span className={cn(
            "flex-shrink-0",
            isActive ? "text-link" : "text-muted-foreground"
          )}>{link.icon}</span>
          {!isCollapsed && <span className="truncate flex-1">{link.label}</span>}
          {!isCollapsed && link.badge !== undefined && (
            <span className="px-1.5 py-0.5 text-[10px] font-bold bg-muted text-muted-foreground rounded-sm">
              {link.badge}
            </span>
          )}
        </div>
        {isExpanded && !isCollapsed && (
          <ul className="mt-0.5 space-y-0.5">
            {children.map(child => renderLink(child, "ml-6"))}
          </ul>
        )}
      </li>
    );
  };

  const renderCategory = (category: NavCategory, index: number) => {
    if (!canRenderNavCategory(category, filterCtx)) return null;

    // Hidden links are gone, and so is a group whose children are all hidden — a header
    // with nothing under it is noise.
    const visibleLinks = filterNavLinks(category.links, filterCtx);

    const isStockCategory = category.category === "STOCK";
    // STOCK survives an empty link list because §4 has an empty state to show instead.
    if (visibleLinks.length === 0 && !(isStockCategory && canSeeStock)) return null;

    return (
      <div key={category.category || `top-${index}`} className={cn(index > 0 ? "mt-5" : "")}>
        {/* Category Header */}
        {category.category && !isCollapsed && (
          <div className="px-3 mb-2">
            <span className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
              {category.category}
            </span>
          </div>
        )}
        {category.category && isCollapsed && (
          <div className="mx-auto mb-2 w-6 border-t border-border" />
        )}
        <ul className="space-y-0.5">
          {visibleLinks.map(link => link.children
            ? renderGroup(link, link.children)
            : renderLink(link))}

          {/* §4 empty states: distinguish "none exist" from "none for you". */}
          {isStockCategory && !isCollapsed && openYards.length === 0 && (
            <li className="px-3 py-2 text-[13px] text-muted-foreground">
              {ability.can(ACTIONS.MANAGE, RESOURCES.STOCK_ADMIN)
                ? 'No yards yet'
                : "You don't have access to any yards"}
            </li>
          )}
        </ul>
      </div>
    );
  };

  return (
    <aside className="flex flex-col h-full bg-card text-sidebar-foreground border-r border-border">
      {/* Logo Header — hidden on mobile (shown in Layout header bar instead) */}
      {!isMobile && (
        <div className={cn(
          "flex items-center h-16 border-b border-border flex-shrink-0",
          isCollapsed ? "justify-center px-2" : "px-5"
        )}>
          {!isCollapsed && (
            <h2 className="text-xl font-semibold tracking-tight text-foreground">
              {getIdarahText()}
            </h2>
          )}
          {isCollapsed && (
            <h2 className="text-lg font-semibold text-foreground">i</h2>
          )}
        </div>
      )}

      {/* Scrollable Navigation Area */}
      <nav className="flex-1 overflow-y-auto min-h-0 custom-scrollbar py-4 px-3">
        {navCategories.map((cat, index) => renderCategory(cat, index))}
      </nav>

      {/* Fixed Bottom Section — Settings & Logout (hidden on mobile, shown in header bar) */}
      {/* {!isMobile && ( */}
      <div className="flex-shrink-0 border-t border-border px-3 py-3">
        <ul className="space-y-0.5">
          {/* Settings (hidden from users still going through onboarding) */}
          {currentUser?.status !== "Onboarding" && (
          <li>
            <Link
              to={settingsLink.href}
              onClick={handleLinkClick}
              className={cn(
                "flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-[14px]",
                isCollapsed ? "justify-center" : "",
                checkActive(settingsLink)
                  ? "bg-selection text-link font-medium"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground"
              )}
              title={isCollapsed ? "Settings" : undefined}
            >
              <span className={cn(
                "flex-shrink-0",
                checkActive(settingsLink) ? "text-link" : "text-muted-foreground"
              )}>{settingsLink.icon}</span>
              {!isCollapsed && <span className="truncate">Settings</span>}
            </Link>
          </li>
          )}
          {/* Logout */}
          <li>
            <Button
              variant="ghost"
              className={cn(
                "w-full rounded-lg transition-all duration-200 text-[14px] h-auto py-2 px-3",
                "text-muted-foreground hover:bg-destructive-bg hover:text-destructive",
                isCollapsed ? "justify-center px-0" : "justify-start"
              )}
              onClick={() => {
                handleLinkClick();
                logout();
              }}
              title={isCollapsed ? "Logout" : undefined}
            >
              <LogOut size={20} className={cn("flex-shrink-0", isCollapsed ? "" : "mr-3")} />
              {!isCollapsed && <span className="truncate">Logout</span>}
            </Button>
          </li>
        </ul>
      </div>
    </aside>
  );
};
